import { toast } from '@heroui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import {
  Button,
  Description,
  Input,
  Label,
  ListBox,
  Modal,
  Select,
  Switch,
  TextArea,
  TextField,
} from '@heroui/react';
import { trpc } from '@/lib/trpc';
import { useEnums } from '@hs/shared';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

const appSchema = z.object({
  name: z.string().min(1, '应用名称不能为空').max(100, '应用名称不能超过100个字符'),
  code: z
    .string()
    .min(1, '应用代码不能为空')
    .max(50, '应用代码不能超过50个字符')
    .regex(/^[a-z-]+$/, '应用代码只能包含小写字母和连字符'),
  description: z.string().nullable().optional(),
  icon: z.string().max(255, '图标URL不能超过255个字符').nullable().optional(),
  order: z.number().int().min(0, '排序必须大于等于0').optional(),
  type: z.enum(
    ['SYSTEM_MANAGEMENT', 'SUBSCRIPTION_MANAGEMENT', 'WORKSPACE'] as [string, ...string[]]
  ),
  status: z.enum(['ACTIVE', 'DISABLED', 'DEPRECATED'] as [string, ...string[]]),
  isVisible: z.boolean().optional(),
  isBuiltin: z.boolean().optional(),
  actions: z.record(z.string(), z.unknown()).nullable().optional(),
});

type AppFormValues = z.infer<typeof appSchema>;

const FIELD_IDS = {
  description: 'editor-app-description',
  actions: 'editor-app-actions',
} as const;

interface AppEditorModalProps {
  open: boolean;
  app?: unknown;
  onCancel: () => void;
  onSuccess: () => void;
}

export function AppEditorModal({ open, app, onCancel, onSuccess }: AppEditorModalProps) {
  const utils = trpc.useUtils();
  const isEdit = !!app;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const { getSortedOptions } = useEnums({
    trpc: trpc as any,
    modules: ['app'],
  });

  /** DialogTrigger 对 `onOpenChange` 引用敏感；父级若每次渲染传入新 `onCancel` 可能误触发关闭并清空 `app`。 */
  const handleModalOpenChange = useCallback((next: boolean) => {
    if (!next) onCancelRef.current();
  }, []);

  const [actionsText, setActionsText] = useState('');

  const form = useForm<AppFormValues>({
    resolver: zodResolver(appSchema),
    defaultValues: {
      name: '',
      code: '',
      description: null,
      icon: null,
      order: 0,
      type: 'SUBSCRIPTION_MANAGEMENT' as const,
      status: 'ACTIVE' as const,
      isVisible: true,
      isBuiltin: false,
      actions: null,
    },
  });
  const { reset } = form;

  const createAppMutation = trpc.bizApp.create.useMutation({
    onSuccess: () => {
      toast.success('应用创建成功');
      form.reset();
      onSuccess();
      void utils.bizApp.list.invalidate();
    },
    onError: (error) => {
      toast.danger('创建失败', {
        description: error.message,
      });
    },
  });

  const updateAppMutation = trpc.bizApp.update.useMutation({
    onSuccess: () => {
      toast.success('应用更新成功');
      form.reset();
      onSuccess();
      void utils.bizApp.list.invalidate();
    },
    onError: (error) => {
      toast.danger('更新失败', {
        description: error.message,
      });
    },
  });

  useEffect(() => {
    if (!open) {
      reset();
      setActionsText('');
      return;
    }

    if (app && typeof app === 'object') {
      const a = app as Record<string, unknown>;
      const actions = (a.actions ?? null) as Record<string, unknown> | null;
      reset({
        name: String(a.name ?? ''),
        code: String(a.code ?? ''),
        description: (a.description as string | null) ?? null,
        icon: (a.icon as string | null) ?? null,
        order: (a.order as number | null) ?? 0,
        type: (a.type || 'SUBSCRIPTION_MANAGEMENT') as AppFormValues['type'],
        status: (a.status || 'ACTIVE') as AppFormValues['status'],
        isVisible: (a.isVisible as boolean | null) ?? true,
        isBuiltin: (a.isBuiltin as boolean | null) ?? false,
        actions,
      });
      setActionsText(actions ? JSON.stringify(actions, null, 2) : '');
    } else {
      reset({
        name: '',
        code: '',
        description: null,
        icon: null,
        order: 0,
        type: 'SUBSCRIPTION_MANAGEMENT',
        status: 'ACTIVE',
        isVisible: true,
        isBuiltin: false,
        actions: null,
      });
      setActionsText('');
    }
  }, [open, app, reset]);

  const onSubmit = async (values: AppFormValues) => {
    if (isEdit && app && typeof app === 'object') {
      const id = String((app as Record<string, unknown>).id ?? '');
      await updateAppMutation.mutateAsync({
        id,
        data: {
          code: values.code,
          name: values.name,
          description: values.description ?? null,
          icon: values.icon ?? null,
          order: values.order ?? 0,
          type: values.type,
          status: values.status,
          isVisible: values.isVisible ?? true,
          isBuiltin: values.isBuiltin ?? false,
          actions: values.actions ?? null,
        },
      });
    } else {
      await createAppMutation.mutateAsync({
        code: values.code,
        name: values.name,
        description: values.description ?? null,
        icon: values.icon ?? null,
        order: values.order ?? 0,
        type: values.type,
        status: values.status,
        isVisible: values.isVisible ?? true,
        isBuiltin: values.isBuiltin ?? false,
        actions: values.actions ?? null,
      });
    }
  };

  const isLoading = createAppMutation.isPending || updateAppMutation.isPending;
  const errors = form.formState.errors;

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="lg">
          <Modal.Dialog
            className="max-h-[90vh]"
            key={
              open
                ? app && typeof app === 'object'
                  ? `edit-${String((app as Record<string, unknown>).id ?? '')}`
                  : 'create'
                : 'closed'
            }
          >
          <Modal.Header className="gap-1">
            <Modal.Heading className="text-xl font-medium">{isEdit ? '编辑应用' : '创建应用'}</Modal.Heading>
            <Description className="text-sm">
              {isEdit ? '修改应用信息' : '填写应用信息以创建新应用'}
            </Description>
          </Modal.Header>
          <Modal.Body className="gap-4 p-2 ">
            <form
              id="editor-app-form"
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex min-w-0 flex-col gap-4"
            >
              <div className="flex flex-col gap-2">
                <Controller
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.name}>
                      <Label className="text-sm font-medium">
                        应用名称 <span className="text-danger">*</span>
                      </Label>
                      <Input
                        maxLength={100}
                        placeholder="请输入应用名称"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    </TextField>
                  )}
                />
                {errors.name ? (
                  <p className="text-danger text-sm">{errors.name.message}</p>
                ) : null}
              </div>

              <div className="flex flex-col gap-2">
                <Controller
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.code}>
                      <Label className="text-sm font-medium">
                        应用代码 <span className="text-danger">*</span>
                      </Label>
                      <Input
                        maxLength={50}
                        placeholder="请输入应用代码（如：user、order）"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    </TextField>
                  )}
                />
                <p className="text-muted-foreground text-xs">
                  只能包含小写字母和连字符（如：user, order, qnaires）
                </p>
                {errors.code ? (
                  <p className="text-danger text-sm">{errors.code.message}</p>
                ) : null}
              </div>

              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium" htmlFor={FIELD_IDS.description}>
                  描述
                </Label>
                <TextArea
                  id={FIELD_IDS.description}
                  aria-label="应用描述"
                  className="resize-none"
                  maxLength={500}
                  placeholder="请输入应用描述（可选）"
                  rows={3}
                  {...form.register('description')}
                />
                {errors.description ? (
                  <p className="text-danger text-sm">{errors.description.message as string}</p>
                ) : null}
              </div>

              <div className="flex flex-col gap-2">
                <Controller
                  control={form.control}
                  name="icon"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.icon}>
                      <Label className="text-sm font-medium">图标</Label>
                      <Input
                        maxLength={255}
                        placeholder="请输入图标URL或图标标识（可选）"
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(e.target.value === '' ? null : e.target.value)
                        }
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    </TextField>
                  )}
                />
                {errors.icon ? <p className="text-danger text-sm">{errors.icon.message}</p> : null}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium">
                    类型 <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onChange={(key) => {
                          if (key != null) field.onChange(String(key));
                        }}
                      >
                        <Select.Trigger className="w-full">
                          <Select.Value />
                          <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                          <ListBox>
                            {getSortedOptions('app', 'type').map((option) => (
                              <ListBox.Item
                                key={option.value}
                                id={option.value}
                                isDisabled={option.disabled}
                                textValue={option.label}
                              >
                                {option.label}
                                <ListBox.ItemIndicator />
                              </ListBox.Item>
                            ))}
                          </ListBox>
                        </Select.Popover>
                      </Select>
                    )}
                  />
                  {errors.type ? (
                    <p className="text-danger text-sm">{errors.type.message}</p>
                  ) : null}
                </div>

                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium">
                    状态 <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onChange={(key) => {
                          if (key != null) field.onChange(String(key));
                        }}
                      >
                        <Select.Trigger className="w-full">
                          <Select.Value />
                          <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                          <ListBox>
                            {getSortedOptions('app', 'status').map((option) => (
                              <ListBox.Item
                                key={option.value}
                                id={option.value}
                                isDisabled={option.disabled}
                                textValue={option.label}
                              >
                                {option.label}
                                <ListBox.ItemIndicator />
                              </ListBox.Item>
                            ))}
                          </ListBox>
                        </Select.Popover>
                      </Select>
                    )}
                  />
                  {errors.status ? (
                    <p className="text-danger text-sm">{errors.status.message}</p>
                  ) : null}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium">是否可见</Label>
                  <Controller
                    control={form.control}
                    name="isVisible"
                    render={({ field }) => (
                      <Switch isSelected={!!field.value} onChange={field.onChange}>
                        <Switch.Content>
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch.Content>
                      </Switch>
                    )}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium">是否内置</Label>
                  <Controller
                    control={form.control}
                    name="isBuiltin"
                    render={({ field }) => (
                      <Switch isSelected={!!field.value} onChange={field.onChange}>
                        <Switch.Content>
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch.Content>
                      </Switch>
                    )}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Controller
                  control={form.control}
                  name="order"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.order}>
                      <Label className="text-sm font-medium">排序</Label>
                      <Input
                        min={0}
                        placeholder="请输入排序值（数字越大越靠前）"
                        type="number"
                        value={field.value === undefined || field.value === null ? '' : field.value}
                        onChange={(e) => {
                          const raw = e.target.value;
                          field.onChange(raw === '' ? 0 : Number(raw));
                        }}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    </TextField>
                  )}
                />
                {errors.order ? (
                  <p className="text-danger text-sm">{errors.order.message}</p>
                ) : null}
              </div>

              <Controller
                control={form.control}
                name="actions"
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <Label className="text-sm font-medium" htmlFor={FIELD_IDS.actions}>
                      应用权限操作配置
                    </Label>
                    <TextArea
                      id={FIELD_IDS.actions}
                      aria-label="应用权限操作 JSON 配置"
                      className="resize-none font-mono text-sm"
                      placeholder='请输入JSON格式的权限操作配置（可选，例如：{"view": "查看", "edit": "编辑"}）'
                      rows={4}
                      style={{ resize: 'vertical' }}
                      value={actionsText}
                      onChange={(e) => {
                        const value = e.target.value;
                        setActionsText(value);
                        if (!value.trim()) {
                          field.onChange(null);
                        } else {
                          try {
                            field.onChange(JSON.parse(value));
                          } catch {
                            /* 输入中允许不完整 JSON */
                          }
                        }
                      }}
                      onBlur={(e) => {
                        field.onBlur();
                        const value = e.target.value.trim();
                        if (!value) {
                          field.onChange(null);
                          setActionsText('');
                        } else {
                          try {
                            const parsed = JSON.parse(value);
                            field.onChange(parsed);
                            setActionsText(JSON.stringify(parsed, null, 2));
                          } catch {
                            /* 保持原文便于修正 */
                          }
                        }
                      }}
                    />
                    <p className="text-muted-foreground text-xs">
                      可选，JSON 格式，简单键值对，键为权限操作代码，值为权限操作名称
                    </p>
                    {errors.actions ? (
                      <p className="text-danger text-sm">{errors.actions.message as string}</p>
                    ) : null}
                  </div>
                )}
              />
            </form>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="ghost" onPress={onCancel}>
              取消
            </Button>
            <Button
              isDisabled={isLoading}
              variant="primary"
              onPress={() => void form.handleSubmit(onSubmit)()}
            >
              {isLoading ? '提交中…' : isEdit ? '更新' : '创建'}
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
